import { useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Save } from "@mui/icons-material";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import {
  Employee,
  EmployeeStatusEventType,
  EmployeeStatusHistoryEntry,
} from "../types/processTypes";
import { useAppDispatch } from "../../../app/hooks";
import { addEmployeeStatusRemark } from "../processSlices";

type Props = {
  employee?: Employee | null;
  loading?: boolean;
  onSaved?: (employee: Employee) => void;
};

function formatDate(value?: Date | string | null) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return format(date, "dd MMM yyyy, HH:mm");
}

function formatChangedBy(email?: string) {
  if (!email) return "";
  const local = email.split("@")[0] ?? email;
  return local.replace(/[._-]+/g, " ").trim();
}

export function EmployeeRemarksDialog({ employee, loading, onSaved }: Props) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const remarks = useMemo(() => {
    const history = employee?.statusHistory ?? [];
    return history
      .filter(
        (e: EmployeeStatusHistoryEntry) =>
          e.eventType === EmployeeStatusEventType.remark_added &&
          e.remark?.trim(),
      )
      .slice()
      .reverse();
  }, [employee?.statusHistory]);

  const handleSave = async () => {
    const trimmed = text.trim();
    if (!trimmed || !employee?._id) return;
    setSubmitting(true);
    const result = await dispatch(
      addEmployeeStatusRemark({ _id: employee._id, remark: trimmed }),
    );
    setSubmitting(false);
    if (addEmployeeStatusRemark.fulfilled.match(result) && result.payload) {
      setText("");
      onSaved?.(result.payload as Employee);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          px: 3,
          py: 8,
        }}
      >
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  return (
    <Stack spacing={2} sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 2.5 } }}>
      <Box>
        <TextField
          label={t("add_remark")}
          value={text}
          onChange={(e) => setText(e.target.value)}
          multiline
          minRows={3}
          fullWidth
          size="small"
        />
        <Button
          color="secondary"
          variant="contained"
          onClick={handleSave}
          disabled={submitting || !text.trim() || !employee?._id}
          sx={{ mt: 1 }}
          startIcon={<Save />}
        >
          {t("save_remark")}
        </Button>
      </Box>

      <Divider />

      {remarks.length === 0 ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ py: 2, textAlign: "center" }}
        >
          {t("no_remarks_yet")}
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {remarks.map((entry, index) => (
            <Box
              key={`${entry.changedAt}-${index}`}
              sx={{
                p: 1.5,
                borderRadius: 1,
                border: 1,
                borderColor: "divider",
                bgcolor: "action.hover",
              }}
            >
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {entry.remark}
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                sx={{ mt: 0.75 }}
                alignItems="center"
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontStyle: "italic" }}
                >
                  {formatChangedBy(entry.changedByEmail)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  •
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatDate(entry.effectiveDate ?? entry.changedAt)}
                </Typography>
              </Stack>
            </Box>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
